import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPreferredAudioInputId, getPreferredAudioOutputId, invariant } from '@streem/toolbox';
import { findMatchingDeviceByGroupId } from '../util/find_matching_device_by_group_id';
const isAudioKind = (kind) => {
    return ['audioinput', 'audiooutput'].includes(kind);
};
const useFindAndSelectMatchingDeviceCreator = (audioDevices, onSelectMicrophone, onSelectSpeaker, setSelectedMicrophone, setSelectedSpeaker) => {
    const findAndSelectMatchingDevice = useCallback((device) => {
        invariant(isAudioKind(device.kind), 'Invalid device kind');
        const kind = device.kind;
        // default to audioinput as device kind (audiooutput as matching device)
        let matchingDeviceKind = 'audiooutput';
        let onSelectMatchingDeviceFn = onSelectSpeaker;
        let setSelectedMatchingDeviceFn = setSelectedSpeaker;
        // override if device kind is audiooutput
        if (kind === 'audiooutput') {
            matchingDeviceKind = 'audioinput';
            onSelectMatchingDeviceFn = onSelectMicrophone;
            setSelectedMatchingDeviceFn = setSelectedMicrophone;
        }
        const matchingDevice = findMatchingDeviceByGroupId(audioDevices, matchingDeviceKind, device.groupId);
        if (matchingDevice) {
            setSelectedMatchingDeviceFn(matchingDevice);
            onSelectMatchingDeviceFn(matchingDevice);
            return true;
        }
        return false;
    }, [
        audioDevices,
        onSelectMicrophone,
        onSelectSpeaker,
        setSelectedMicrophone,
        setSelectedSpeaker,
    ]);
    return findAndSelectMatchingDevice;
};
// This hook is currently only used for in-call, but in an attempt to make it more generic, it accepts
//      onSelectMicrophone and onSelectSpeaker functions as arguments.  (For the in-call flow,
//      selecting a microphone or speaker uses the mediaController, but outside of a call, it will
//      use getUserMedia/setSinkId)
export const useSelectedAudioDevices = (canSelectSpeaker, onSelectMicrophone, onSelectSpeaker, localDevices, localAudioLabel) => {
    const audioDevices = useMemo(() => {
        return (localDevices?.filter(device => ['audiooutput', 'audioinput'].includes(device.kind)) ||
            []);
    }, [localDevices]);
    const [selectedMicrophone, setSelectedMicrophone] = useState(null);
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    const findAndSelectMatchingDevice = useFindAndSelectMatchingDeviceCreator(audioDevices, onSelectMicrophone, onSelectSpeaker, setSelectedMicrophone, setSelectedSpeaker);
    useEffect(() => {
        // if we have a preferred microphone selected, set it as the selected microphone
        const preferredMicrophoneId = getPreferredAudioInputId();
        const preferredSpeakerId = getPreferredAudioOutputId();
        const preferredMicrophone = preferredMicrophoneId &&
            audioDevices.find(device => device.deviceId === preferredMicrophoneId && device.kind === 'audioinput');
        const preferredSpeaker = preferredSpeakerId &&
            audioDevices.find(device => device.deviceId === preferredSpeakerId && device.kind === 'audiooutput');
        if (preferredSpeaker) {
            setSelectedSpeaker(preferredSpeaker);
            if (canSelectSpeaker) {
                if (!preferredMicrophone) {
                    const foundMatchingDevice = findAndSelectMatchingDevice(preferredSpeaker);
                    if (!foundMatchingDevice) {
                        const currentMicrophone = audioDevices.find(device => device.label === localAudioLabel);
                        setSelectedMicrophone(currentMicrophone || null);
                    }
                }
                else {
                    // Microphone and speaker can be indepenedent of each other and don't have to belong
                    // to the same group.
                    setSelectedMicrophone(preferredMicrophone);
                }
            }
        }
        else if (preferredMicrophone) {
            setSelectedMicrophone(preferredMicrophone);
            if (canSelectSpeaker) {
                // if we hit this branch of the if statement, we know that there is no preferred speaker.  Look for a group match for
                //     the preferred microphone and if there is one, set it as the selected speaker
                findAndSelectMatchingDevice(preferredMicrophone);
            }
        }
        else if (localAudioLabel && audioDevices.length > 0) {
            // if we hit this branch of the if statement, we know that there is no preferred microphone or speaker.  Use the currently
            //    selected microphone (based on mediaController.localAudioLabel)
            const currentMicrophone = audioDevices.find(device => device.label === localAudioLabel);
            setSelectedMicrophone(currentMicrophone || null);
            if (currentMicrophone && canSelectSpeaker) {
                // if there is a current microphone, try to find a matching speaker.  If there is no matching speaker, do not change the
                //     selected speaker.
                findAndSelectMatchingDevice(currentMicrophone);
            }
        }
    }, [
        audioDevices,
        localAudioLabel,
        canSelectSpeaker,
        findAndSelectMatchingDevice,
        setSelectedMicrophone,
        setSelectedSpeaker,
    ]);
    return {
        selectedMicrophone,
        setSelectedMicrophone,
        selectedSpeaker,
        setSelectedSpeaker,
    };
};
//# sourceMappingURL=use_selected_audio_devices.js.map